import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import liff from '@line/liff';
import { api } from '../lib/api';
import { fmtDateTime, fmtDuration } from '../lib/format';

interface Photo {
  id: string;
  phase: 'start' | 'end';
  seq: number;
  url: string;
}

interface DutyLog {
  id: string;
  locationName: string;
  latitude: number;
  longitude: number;
  startAt: string;
  endAt: string | null;
  durationMinutes: number | null;
  note: string | null;
  status: string;
  dutyType: { name: string };
  photos: Photo[];
}

export function DutyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [duty, setDuty] = useState<DutyLog | null>(null);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    if (!id) return;
    api.get<DutyLog>(`/app/duty-logs/${id}`).then((r) => setDuty(r.data));
  }, [id]);

  async function share() {
    if (!duty) return;
    if (typeof liff === 'undefined' || !liff.isApiAvailable?.('shareTargetPicker')) {
      alert('ไม่สามารถแชร์ผ่าน LINE ในขณะนี้ (เปิดจาก LINE webview เท่านั้น)');
      return;
    }
    setSharing(true);
    try {
      const heroUrl = duty.photos[0]?.url;
      const msg = {
        type: 'flex' as const,
        altText: `บันทึกการปฏิบัติงาน — ${duty.locationName}`,
        contents: {
          type: 'bubble',
          ...(heroUrl
            ? {
                hero: {
                  type: 'image',
                  url: heroUrl,
                  size: 'full',
                  aspectMode: 'cover',
                  aspectRatio: '4:3',
                },
              }
            : {}),
          body: {
            type: 'box',
            layout: 'vertical',
            spacing: 'sm',
            contents: [
              {
                type: 'text',
                text: duty.locationName,
                weight: 'bold',
                size: 'lg',
                wrap: true,
              },
              { type: 'text', text: duty.dutyType.name, color: '#888888', size: 'sm' },
              {
                type: 'text',
                text: `${fmtDateTime(duty.startAt)} • ${fmtDuration(duty.durationMinutes)}`,
                size: 'xs',
                color: '#555555',
                wrap: true,
              },
              ...(duty.note
                ? [
                    {
                      type: 'text' as const,
                      text: duty.note,
                      size: 'sm' as const,
                      wrap: true,
                      margin: 'md' as const,
                    },
                  ]
                : []),
            ],
          },
          footer: {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'text',
                text: 'อปพร. เทศบาลนครนครปฐม',
                size: 'xs',
                color: '#aaaaaa',
                align: 'center',
              },
            ],
          },
        },
      };
      // any-cast เพราะ liff types เก่า
      const result = await (liff as unknown as { shareTargetPicker: (msgs: unknown[]) => Promise<unknown> })
        .shareTargetPicker([msg]);
      void result;
    } catch (err) {
      alert(err instanceof Error ? err.message : 'แชร์ไม่สำเร็จ');
    } finally {
      setSharing(false);
    }
  }

  if (!duty) {
    return <div className="p-8 text-center text-sm text-gray-500">กำลังโหลด...</div>;
  }

  const start = duty.photos.filter((p) => p.phase === 'start');
  const end = duty.photos.filter((p) => p.phase === 'end');

  return (
    <div className="mx-auto min-h-screen max-w-md bg-gray-50">
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-gray-200 bg-white px-4 py-3">
        <button onClick={() => navigate(-1)} className="text-2xl text-gray-500">
          ‹
        </button>
        <h1 className="text-base font-semibold">รายละเอียดบันทึก</h1>
      </header>

      <main className="space-y-4 p-4">
        <div className="card">
          <p className="text-xs text-gray-500">{duty.dutyType.name}</p>
          <h2 className="mt-1 text-xl font-bold">{duty.locationName}</h2>
          <p className="mt-1 text-xs text-gray-500">สถานะ: {duty.status}</p>
        </div>

        <div className="card grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-gray-500">เริ่ม</p>
            <p className="font-medium">{fmtDateTime(duty.startAt)}</p>
          </div>
          <div>
            <p className="text-gray-500">เลิก</p>
            <p className="font-medium">{fmtDateTime(duty.endAt)}</p>
          </div>
          <div className="col-span-2">
            <p className="text-gray-500">ระยะเวลา</p>
            <p className="font-medium text-line">{fmtDuration(duty.durationMinutes)}</p>
          </div>
          <div className="col-span-2">
            <p className="text-gray-500">พิกัด</p>
            <p className="font-mono text-xs">
              {duty.latitude.toFixed(5)}, {duty.longitude.toFixed(5)}
            </p>
            <a
              href={`https://www.google.com/maps?q=${duty.latitude},${duty.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-block text-xs text-line underline"
            >
              เปิดใน Google Maps
            </a>
          </div>
          {duty.note && (
            <div className="col-span-2">
              <p className="text-gray-500">หมายเหตุ</p>
              <p>{duty.note}</p>
            </div>
          )}
        </div>

        {start.length > 0 && (
          <div className="card">
            <p className="label">รูปตอนเริ่มงาน</p>
            <div className="grid grid-cols-2 gap-3">
              {start.map((p) => (
                <a
                  key={p.id}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="aspect-square overflow-hidden rounded-2xl bg-gray-100"
                >
                  <img src={p.url} alt="" className="h-full w-full object-cover" />
                </a>
              ))}
            </div>
          </div>
        )}

        {end.length > 0 && (
          <div className="card">
            <p className="label">รูปตอนจบงาน</p>
            <div className="grid grid-cols-2 gap-3">
              {end.map((p) => (
                <a
                  key={p.id}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="aspect-square overflow-hidden rounded-2xl bg-gray-100"
                >
                  <img src={p.url} alt="" className="h-full w-full object-cover" />
                </a>
              ))}
            </div>
          </div>
        )}

        <button onClick={share} disabled={sharing} className="btn-primary">
          {sharing ? 'กำลังแชร์...' : '📤 แชร์ไป LINE'}
        </button>
      </main>
    </div>
  );
}
